import { Box, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import DateTimePicker from '@/components/shared/DateTimePicker';
import ErrorPage from '@/components/shared/ErrorPage';
import ProgressBar from '@/components/shared/ProgressBar';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useProgress from '@/hooks/useProgress';
import { Page } from '@/lib/tagging';
import { mapCommPref } from '@/lib/tagging/commPrefMapping';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelected,
  isOnlineAppointment as isOnlineAppointmentSelected,
} from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import useAgentsAndTimeslots from '../hooks/useAgentsAndTimeslots';
import useTimeSlotSelection from '../hooks/useTimeSlotSelection';

const CalendarSelection: React.FC = () => {
  const tagging = useTagging();
  const {
    timeSlots,
    isLoading: isLoadingTimeSlots,
    error: timeSlotsError,
    refetch: refetchTimeSlots,
  } = useAgentsAndTimeslots();
  const navigate = useNavigate();
  const { id } = useParams();

  useLandingPageRedirection();
  useProgress(40);
  const [tagged, setTagged] = useState(false);

  const {
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    handleDateChange,
    handleTimeSlotChange,
  } = useTimeSlotSelection(timeSlots);

  const { selectedDestination, searchedDestination, contactInfo, userSearch } =
    useAppointmentStore();

  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelected,
  );
  const isOnlineAppointment = useAppointmentStore(isOnlineAppointmentSelected);
  const { sessionId, sessionSource } = useSessionStore();

  useEffect(() => {
    if (!tagged) {
      const trvEndLocation =
        searchedDestination || userSearch || selectedDestination.name;

      tagging.tag('view', sessionSource, {
        page: Page.selectAppointment,
        sessionid: sessionId,
        flow: '',
        trv_end_location: trvEndLocation,
        comm_pref: mapCommPref(
          contactInfo.communicationPreference,
          isInBranchAppointment,
          isOnlineAppointment,
        ),
      });

      setTagged(true);
    }
  }, [
    tagged,
    searchedDestination,
    selectedDestination.name,
    sessionSource,
    sessionId,
    tagging.tag,
    contactInfo.communicationPreference,
    isInBranchAppointment,
    isOnlineAppointment,
    userSearch,
  ]);

  const handleClickContinue = () => {
    if (selectedDate && selectedStartTime && selectedEndTime) {
      navigate(`/schedule-appointment/${id}/agent-selection`);
    }
  };

  const isButtonDisabled =
    !selectedDate || !selectedStartTime || !selectedEndTime;

  if (timeSlotsError) {
    return <ErrorPage onClickTryAgain={refetchTimeSlots} />;
  }

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
        }}
      >
        <Stack
          spacing={6}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            pt: 4,
            minHeight: '100%',
          }}
        >
          <Stack
            spacing={0}
            sx={{
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography variant='h4'>
              Select date & time to speak with an advisor
            </Typography>
            <Typography
              variant='b1Regular'
              sx={{
                color: 'grey.800',
                fontWeight: 500,
                fontSize: {
                  lg: '1.25rem',
                },
              }}
            >
              Book an appointment
            </Typography>
          </Stack>

          <DateTimePicker
            timeSlots={timeSlots}
            selectedDate={selectedDate}
            selectedTimeSlot={{
              startTime: selectedStartTime || '',
              endTime: selectedEndTime || '',
            }}
            onChangeDate={handleDateChange}
            onChangeTimeSlot={handleTimeSlotChange}
            disabled={false}
            isLoading={isLoadingTimeSlots}
          />
        </Stack>
      </Box>

      <ProgressBar
        buttonDisabled={isButtonDisabled}
        displayBackButton={true}
        onClickContinue={handleClickContinue}
      />
    </Stack>
  );
};

export default CalendarSelection;
